import { z } from 'zod';

const CYCLE_ID_RE = /^(MONTHLY-[0-9]{4}-[0-9]{2}|WEEKLY-[0-9]{4}-W[0-9]{2})$/;
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const optionalString = (max: number, message: string) =>
  z.string().trim().max(max, { message }).optional().or(z.literal(''));

export const settlementGenerateSchema = z.object({
  cycleId: z
    .string()
    .trim()
    .min(1, { message: 'settlement.errors.cycleIdRequired' })
    .regex(CYCLE_ID_RE, { message: 'settlement.errors.cycleIdInvalid' }),
  vendorIds: z.string().trim().optional().or(z.literal('')),
  note: optionalString(500, 'settlement.errors.noteTooLong'),
});

export type SettlementGenerateValues = z.infer<typeof settlementGenerateSchema>;

export const emptySettlementGenerateValues = (): SettlementGenerateValues => ({
  cycleId: '',
  vendorIds: '',
  note: '',
});

export const parseVendorIds = (input: string): string[] =>
  input
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 100);

export const settlementApproveSchema = z.object({
  note: optionalString(500, 'settlement.errors.noteTooLong'),
});

export type SettlementApproveValues = z.infer<typeof settlementApproveSchema>;

export const settlementPayoutSchema = z.object({
  bankAccountId: z
    .string()
    .trim()
    .refine((v) => v === '' || UUID_RE.test(v), {
      message: 'settlement.errors.productIdInvalid',
    })
    .optional()
    .or(z.literal('')),
  note: optionalString(500, 'settlement.errors.noteTooLong'),
});

export type SettlementPayoutValues = z.infer<typeof settlementPayoutSchema>;

export const releaseScheduleCreateSchema = z.object({
  vendorId: z
    .string()
    .trim()
    .min(1, { message: 'settlement.errors.vendorIdRequired' })
    .max(32, { message: 'settlement.errors.vendorIdTooLong' }),
  artistId: z
    .string()
    .trim()
    .refine((v) => v === '' || UUID_RE.test(v), {
      message: 'settlement.errors.artistIdInvalid',
    })
    .optional()
    .or(z.literal('')),
  productId: z
    .string()
    .trim()
    .refine((v) => v === '' || UUID_RE.test(v), {
      message: 'settlement.errors.productIdInvalid',
    })
    .optional()
    .or(z.literal('')),
  title: z
    .string()
    .trim()
    .min(1, { message: 'settlement.errors.titleRequired' })
    .max(200, { message: 'settlement.errors.titleTooLong' }),
  description: optionalString(4000, 'settlement.errors.descriptionTooLong'),
  releaseAt: z
    .string()
    .trim()
    .min(1, { message: 'settlement.errors.releaseAtRequired' })
    .regex(DATE_TIME_RE, { message: 'settlement.errors.releaseAtInvalid' }),
});

export type ReleaseScheduleCreateValues = z.infer<typeof releaseScheduleCreateSchema>;

export const emptyReleaseScheduleCreateValues = (): ReleaseScheduleCreateValues => ({
  vendorId: '',
  artistId: '',
  productId: '',
  title: '',
  description: '',
  releaseAt: '',
});

export const releaseScheduleUpdateSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, { message: 'settlement.errors.titleRequired' })
      .max(200, { message: 'settlement.errors.titleTooLong' })
      .optional(),
    description: optionalString(4000, 'settlement.errors.descriptionTooLong'),
    artistId: z
      .string()
      .trim()
      .refine((v) => v === '' || UUID_RE.test(v), {
        message: 'settlement.errors.artistIdInvalid',
      })
      .optional()
      .or(z.literal('')),
    productId: z
      .string()
      .trim()
      .refine((v) => v === '' || UUID_RE.test(v), {
        message: 'settlement.errors.productIdInvalid',
      })
      .optional()
      .or(z.literal('')),
    releaseAt: z
      .string()
      .trim()
      .refine((v) => v === '' || DATE_TIME_RE.test(v), {
        message: 'settlement.errors.releaseAtInvalid',
      })
      .optional()
      .or(z.literal('')),
    delayReason: optionalString(500, 'settlement.errors.cancelReasonTooLong'),
    isPublishedRun: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const anyField =
      (data.title && data.title.length > 0) ||
      (data.description !== undefined && data.description !== '') ||
      (data.artistId !== undefined && data.artistId !== '') ||
      (data.productId !== undefined && data.productId !== '') ||
      (data.releaseAt !== undefined && data.releaseAt !== '') ||
      (data.delayReason !== undefined && data.delayReason !== '');
    if (!anyField) {
      ctx.addIssue({
        code: 'custom',
        path: ['title'],
        message: 'settlement.errors.updateMinOne',
      });
    }
    if (data.isPublishedRun && data.releaseAt && data.releaseAt.length > 0) {
      if (!data.delayReason || data.delayReason.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['delayReason'],
          message: 'settlement.errors.delayReasonRequired',
        });
      }
    }
  });

export type ReleaseScheduleUpdateValues = z.infer<typeof releaseScheduleUpdateSchema>;

export const releaseScheduleCancelSchema = z.object({
  cancelReason: z
    .string()
    .trim()
    .min(1, { message: 'settlement.errors.cancelReasonRequired' })
    .max(500, { message: 'settlement.errors.cancelReasonTooLong' }),
});

export type ReleaseScheduleCancelValues = z.infer<typeof releaseScheduleCancelSchema>;
